<template>
  <v-main>
    <v-container v-if="projectData" fluid @keydown.ctrl.s.prevent="updateTimesheet" @keydown.meta.s.prevent="updateTimesheet">
      <h1>{{ projectData.projectName }}</h1>
      <v-row class="mt-4" dense>
        <v-col v-for="tile in projectTiles" :key="tile.route" cols="12" md="3">
          <v-card class="tile-card" :color="tile.color" :to="{ name: tile.route, params: { id: projectId } }">
            <v-card-title class="headline"><v-icon class="mr-3">{{ tile.icon }}</v-icon>{{ tile.title }}</v-card-title>
            <v-card-text>{{ tile.description }}</v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <section class="timesheet-editor" aria-label="Timesheet editor">
        <div class="editor-toolbar">
          <div class="month-navigation">
            <v-btn variant="text" icon="mdi-chevron-left" aria-label="Previous month" :disabled="isUpdating" @click="changeMonth(-1)" />
            <label class="month-picker">Month
              <input v-model="selectedMonth" type="month" min="1900-01" :max="maximumMonth" :disabled="isUpdating" aria-label="Timesheet month" />
            </label>
            <v-btn variant="text" icon="mdi-chevron-right" aria-label="Next month" :disabled="isUpdating || selectedMonth >= maximumMonth" @click="changeMonth(1)" />
          </div>
          <div class="editor-summary" aria-live="polite">
            <strong>{{ displayedHours }}h</strong><span>{{ displayedDays }} days</span>
          </div>
          <div class="save-controls">
            <span class="save-status" role="status">{{ saveStatus }}</span>
            <v-btn variant="text" icon="mdi-refresh" aria-label="Reload this month" :disabled="isUpdating" @click="reloadMonth" />
            <v-btn color="primary" prepend-icon="mdi-content-save-outline" :loading="isUpdating" :disabled="!canSave" @click="updateTimesheet">Save changes</v-btn>
          </div>
        </div>

        <v-alert v-if="sheet.error" type="error" variant="tonal" class="my-3">{{ sheet.error }}</v-alert>
        <v-alert v-if="sheet.remoteChanged" type="warning" variant="tonal" class="my-3">Saved hours changed elsewhere. Your edits are kept; saving will check for conflicts.</v-alert>
        <p v-if="otherDrafts" class="draft-notice">{{ otherDrafts }} other {{ otherDrafts === 1 ? 'month has' : 'months have' }} unsaved edits. Switch back to save them before leaving this page.</p>
        <p v-if="sheet.ready && !sheet.exists" class="draft-notice">New timesheet using your project schedule and holidays. Review the hours and save to keep them.</p>
        <v-progress-linear v-if="!sheet.ready && !sheet.error" indeterminate color="primary" aria-label="Loading timesheet" />

        <div v-if="sheet.ready" class="timesheet-table-wrap">
          <table class="timesheet-table">
            <caption class="sr-only">{{ monthYearLabel }} timesheet for {{ projectData.projectName }}</caption>
            <thead><tr><th scope="col">Date</th><th scope="col">Day</th><th scope="col">Hours worked</th><th scope="col">Comments</th></tr></thead>
            <tbody>
              <tr v-for="entry in sheet.entries" :key="entry.date" :data-date="entry.date" :class="{ 'greyed-out': isWeekend(entry.date) }">
                <td class="date-cell">{{ entry.date }}</td>
                <td class="weekday-cell">{{ weekdayLabel(entry.date) }}</td>
                <td>
                  <div class="hours-cell">
                    <input v-model.number="entry.hoursWorked" class="hours-input" :class="{ 'zero-hours': entry.hoursWorked === 0 }" type="number" inputmode="decimal" min="0" max="24" step="0.1" :disabled="isUpdating" :aria-label="`Hours for ${entry.date}`" />
                    <div class="quick-hours">
                      <button v-for="hours in quickHours" :key="hours" type="button" :disabled="isUpdating" :aria-label="`Set ${hours} hours for ${entry.date}`" :aria-pressed="entry.hoursWorked === hours" :class="{ selected: entry.hoursWorked === hours }" @click="entry.hoursWorked = hours">{{ hours }}h</button>
                    </div>
                  </div>
                </td>
                <td><input v-model="entry.publicComments" class="comments-input" type="text" :disabled="isUpdating" :aria-label="`Comments for ${entry.date}`" placeholder="Add a comment" /></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="editor-hint">Edits stay here when switching months. Save changes to update your timesheet; use the Timesheets page when you want to generate and email files.</p>
      </section>
      <v-snackbar v-model="showSuccessSnackbar" color="success" timeout="3000">Timesheet saved.</v-snackbar>
    </v-container>
    <v-container v-else>
      <v-alert v-if="projectError" type="error" variant="tonal">{{ projectError }}</v-alert>
      <v-progress-linear v-else indeterminate color="primary" aria-label="Loading project" />
    </v-container>
  </v-main>
</template>

<script>
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { readProject, cachedTimesheet, readTimesheet, saveTimesheet } from '@/services/timesheetEditor'
import { entryChanges, totalHours } from '@/utils/timesheetEditor.cjs'

const monthString = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
const copyEntries = entries => entries.map(entry => ({ ...entry }))
const dirty = sheet => sheet?.ready && entryChanges(sheet.baseline, sheet.entries).length > 0
const weekdayFormat = new Intl.DateTimeFormat(undefined, { weekday: 'short', timeZone: 'UTC' })

export default {
  name: 'ProjectOverviewPage',
  data() {
    return {
      userId: '', projectId: '', projectData: null, projectPromise: null, projectError: '',
      selectedMonth: monthString(new Date()), sheets: {}, epoch: 0, disposed: false,
      isUpdating: false, showSuccessSnackbar: false, stopAuth: null,
      projectTiles: [
        { route: 'ProjectTimesheets', title: 'Timesheets', icon: 'mdi-file-clock', color: 'blue', description: 'View and manage your timesheets.' },
        { route: 'ProjectInvoices', title: 'Invoices', icon: 'mdi-file-document', color: 'green', description: 'View and manage your invoices.' },
        { route: 'ProjectDetails', title: 'Project Details', icon: 'mdi-cog-outline', color: 'orange', description: 'Configure your project settings.' },
        { route: 'ProjectStats', title: 'Project Stats', icon: 'mdi-chart-box-outline', color: 'purple', description: 'Hours, days and revenue before VAT.' }
      ]
    }
  },
  computed: {
    monthId() { return this.selectedMonth.replace('-', '') },
    sheet() { return this.sheets[this.monthId] || { entries: [], baseline: [], ready: false } },
    defaultHours() { return Number(this.projectData?.projectDetails?.workDayHours) || 8 },
    quickHours() { return [...new Set([0, 4, this.defaultHours])] },
    displayedHours() { return totalHours(this.sheet.entries).toLocaleString(undefined, { maximumFractionDigits: 2 }) },
    displayedDays() { return (totalHours(this.sheet.entries) / this.defaultHours).toLocaleString(undefined, { maximumFractionDigits: 2 }) },
    monthYearLabel() { return new Date(`${this.selectedMonth}-01T12:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) },
    maximumMonth() { const now = new Date(); return monthString(new Date(now.getFullYear(), now.getMonth() + 3, 1)) },
    canSave() { return this.sheet.ready && !this.isUpdating && (!this.sheet.exists || dirty(this.sheet)) },
    otherDrafts() { return Object.entries(this.sheets).filter(([id, sheet]) => id !== this.monthId && dirty(sheet)).length },
    hasUnsavedChanges() { return Object.values(this.sheets).some(dirty) },
    saveStatus() {
      if (this.isUpdating) return 'Saving…'
      if (!this.sheet.ready) return this.sheet.error ? 'Could not load' : 'Loading…'
      if (dirty(this.sheet)) return 'Unsaved changes'
      if (!this.sheet.exists) return 'Not saved yet'
      return this.sheet.refreshing ? 'Checking for updates…' : 'All changes saved'
    }
  },
  watch: {
    selectedMonth(value, previous) {
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(value) || value < '1900-01' || value > this.maximumMonth) {
        this.selectedMonth = previous
        return
      }
      if (this.projectPromise) this.loadMonth()
    },
    '$route.params.id'() { if (this.userId) this.openProject() }
  },
  created() {
    this.stopAuth = onAuthStateChanged(getAuth(), user => {
      if (!user) { this.$router.replace('/login'); return }
      this.userId = user.uid
      this.openProject()
    })
    window.addEventListener('beforeunload', this.warnBeforeUnload)
  },
  beforeUnmount() {
    this.disposed = true
    this.epoch++
    this.stopAuth?.()
    window.removeEventListener('beforeunload', this.warnBeforeUnload)
  },
  beforeRouteLeave() { return this.mayLeave() },
  beforeRouteUpdate(to, from) { return to.params.id === from.params.id || this.mayLeave() },
  methods: {
    async openProject() {
      const epoch = ++this.epoch
      this.projectId = this.$route.params.id
      this.projectData = null
      this.projectError = ''
      this.sheets = {}
      const showProject = project => { if (!this.disposed && epoch === this.epoch) this.projectData = project }
      this.projectPromise = readProject(this.userId, this.projectId, showProject).then(project => {
        showProject(project)
        return project
      })
      try {
        // The project and the selected month's hours are independent reads.
        await Promise.all([this.projectPromise, this.loadMonth()])
      } catch (error) {
        if (!this.disposed && epoch === this.epoch) {
          this.projectData = null
          this.projectError = error.message || 'Could not load this project. Please refresh.'
        }
      }
    },
    async loadMonth({ reload = false } = {}) {
      const epoch = this.epoch
      const id = this.monthId
      if (!this.sheets[id]) this.sheets[id] = { entries: [], baseline: [], ready: false, exists: false, request: 0, error: '', remoteChanged: false }
      const sheet = this.sheets[id]
      const cached = !reload && cachedTimesheet(this.userId, this.projectId, id)
      if (!sheet.ready && cached) this.applySheet(sheet, cached.data, cached.exists)
      const request = ++sheet.request
      sheet.refreshing = true
      sheet.error = ''
      try {
        // Paint saved/preloaded rows now; check the server without disabling
        // editing. Transactions still verify the baseline when saving.
        const result = await readTimesheet(this.userId, this.projectId, id, this.projectPromise, { force: true })
        if (this.disposed || epoch !== this.epoch || request !== sheet.request) return
        if (!dirty(sheet)) this.applySheet(sheet, result.data, result.exists)
        else sheet.remoteChanged = sheet.exists !== result.exists || entryChanges(sheet.baseline, result.data.entries).length > 0 || sheet.baseline.length !== result.data.entries.length
      } catch (error) {
        if (!this.disposed && epoch === this.epoch && request === sheet.request) {
          console.error('Timesheet read failed', error)
          sheet.error = 'Could not load this month. ' + error.message
        }
      } finally {
        if (request === sheet.request) sheet.refreshing = false
      }
    },
    applySheet(sheet, data, exists = true) {
      Object.assign(sheet, { entries: copyEntries(data.entries), baseline: copyEntries(data.entries), startDate: data.startDate, endDate: data.endDate, ready: true, exists, remoteChanged: false, error: '' })
    },
    async updateTimesheet() {
      if (!this.canSave) return
      const sheet = this.sheet
      const id = this.monthId
      sheet.request++ // A background refresh must never replace a just-saved edit.
      sheet.refreshing = false
      sheet.error = ''
      this.isUpdating = true
      try {
        const data = await saveTimesheet(this.userId, this.projectId, id, sheet)
        this.applySheet(sheet, data)
        this.showSuccessSnackbar = true
      } catch (error) {
        console.error('Timesheet save failed', error)
        sheet.error = 'Could not save. ' + error.message
      } finally {
        this.isUpdating = false
      }
    },
    changeMonth(offset) {
      if (this.isUpdating) return
      const [year, month] = this.selectedMonth.split('-').map(Number)
      this.selectedMonth = monthString(new Date(year, month - 1 + offset, 1))
    },
    reloadMonth() {
      if (this.isUpdating || (dirty(this.sheet) && !window.confirm('Discard unsaved edits for this month and reload the saved hours?'))) return
      delete this.sheets[this.monthId]
      this.loadMonth({ reload: true })
    },
    mayLeave() {
      if (this.isUpdating) return false
      return !this.hasUnsavedChanges || window.confirm('You have unsaved timesheet edits. Leave this page and discard them?')
    },
    warnBeforeUnload(event) {
      if (this.hasUnsavedChanges || this.isUpdating) { event.preventDefault(); event.returnValue = '' }
    },
    isWeekend(date) { return [0, 6].includes(new Date(`${date}T12:00:00Z`).getUTCDay()) },
    weekdayLabel(date) { return weekdayFormat.format(new Date(`${date}T12:00:00Z`)) }
  }
}
</script>

<style scoped>
.tile-card { min-height: 150px; display: flex; flex-direction: column; justify-content: center; }
.timesheet-editor { margin-top: 24px; }
.editor-toolbar { position: sticky; top: 64px; z-index: 3; display: flex; align-items: center; flex-wrap: wrap; gap: 16px; padding: 12px 0; background: white; border-bottom: 1px solid #dce4e7; }
.month-navigation, .save-controls, .editor-summary { display: flex; align-items: center; gap: 12px; }
.month-picker { display: flex; flex-direction: column; font-size: 10px; font-weight: 600; color: #637781; }
.month-picker input { color: #243646; font: inherit; font-size: 16px; padding: 3px 0; cursor: pointer; }
.editor-summary { margin-right: auto; font-size: 13px; color: #677a83; }
.editor-summary strong { font-size: 20px; color: #243646; }
.save-status { font-size: 12px; color: #647881; }
.timesheet-table-wrap { overflow-x: auto; border: 1px solid #e1e8ea; border-radius: 8px; margin-top: 12px; }
.timesheet-table { width: 100%; border-collapse: collapse; text-align: left; font-size: 14px; }
.timesheet-table th { padding: 12px; background: #f1f5f5; color: #4d626e; font-size: 12px; font-weight: 600; white-space: nowrap; }
.timesheet-table td { padding: 7px 12px; border-top: 1px solid #e8edef; }
.date-cell { width: 120px; white-space: nowrap; font-variant-numeric: tabular-nums; }
.weekday-cell { width: 70px; color: #657580; }
.greyed-out { background: #f6f7f8; }
.hours-cell { display: flex; align-items: center; gap: 12px; }
.hours-input, .comments-input { border: 1px solid #dbe3e7; border-radius: 6px; padding: 7px 9px; font: inherit; background: white; }
.hours-input { width: 84px; font-variant-numeric: tabular-nums; }
.zero-hours { color: #ab4343; }
.comments-input { width: 100%; min-width: 180px; }
.comments-input::placeholder { color: #99a6ad; }
.hours-input:focus, .comments-input:focus, .month-picker input:focus-visible { outline: 2px solid #2f7c7a; outline-offset: 1px; }
.quick-hours { display: flex; gap: 4px; }
.quick-hours button { padding: 4px 7px; border: 1px solid #dce5e8; border-radius: 5px; font-size: 11px; background: white; color: #647882; white-space: nowrap; }
.quick-hours button.selected { background: #e0f1e8; border-color: #90c4a7; color: #275e40; }
.quick-hours button:focus-visible { outline: 2px solid #2f7c7a; }
.quick-hours button:disabled, input:disabled { opacity: .55; }
.editor-hint, .draft-notice { font-size: 12px; color: #657985; margin: 12px 0; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
@media (max-width: 700px) { .editor-toolbar { gap: 10px; } .save-controls { width: 100%; justify-content: flex-end; } .save-status { margin-right: auto; } .timesheet-table td, .timesheet-table th { padding: 6px; } .hours-cell { gap: 6px; } .hours-input { width: 70px; } }
</style>
